/**
 * API Service Layer for FireGuard
 * Handles all HTTP requests to the backend API
 */

export interface ApiError {
  error: string;
  code: string;
}

export interface AuthResponse {
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    organization_id?: string;
  };
  organization?: {
    id: string;
    name: string;
  };
  token: string;
}

export interface Location {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  created_at: number;
}

export interface Extinguisher {
  id: string;
  qr_code: string;
  location_id: string;
  /** The fire class the unit is rated for. Drives the inspection schedule. */
  extinguisher_type: string;
  /** What is in the cylinder, when the record knows. Null when nobody said. */
  agent?: string | null;
  size_lbs: number;
  room_area: string;
  install_date: string;
  last_monthly_inspection: string | null;
  next_monthly_inspection: string | null;
  last_annual_maintenance?: string | null;
  next_annual_maintenance?: string | null;
  last_six_year_exam?: string | null;
  next_six_year_exam?: string | null;
  last_hydrostatic_test?: string | null;
  next_hydrostatic_test?: string | null;
  compliance_status: 'compliant' | 'warning' | 'overdue';
  status: 'active' | 'removed' | 'replaced';
  // Triage fields, computed server-side by describeCompliance()
  compliance_reason?: string;
  compliance_summary?: string;
  compliance_actor?: 'staff' | 'contractor' | 'nobody';
  days_overdue?: number;
}

export interface Inspection {
  id: string;
  extinguisher_id: string;
  inspection_type: 'monthly' | 'annual' | 'six_year' | 'hydrostatic';
  inspection_date: string;
  inspector_name: string;
  status: 'pass' | 'fail';
  pressure_gauge_status: 'good' | 'low' | 'high';
  seal_intact: number;
  findings: string | null;
  photo_url: string | null;
}

export interface BillingEntitlements {
  planId: string;
  planName: string;
  status: string;
  isTrial: boolean;
  trialEndsAt: number | null;
  trialDaysLeft: number | null;
  maxExtinguishers: number;
  activeExtinguishers: number;
  remaining: number;
  overLimit: boolean;
  graceEndsAt: number | null;
  canAddExtinguisher: boolean;
  readOnly: boolean;
  interval: 'month' | 'year' | null;
  currentPeriodEnd: number | null;
  cancelAt: number | null;
  hasStripeCustomer: boolean;
  blockReason?: string;
}

export interface BillingInfo {
  entitlements: BillingEntitlements;
  plans: Array<{ id: string; name: string; monthlyUsd: number; annualUsd: number; maxExtinguishers: number; tagline: string }>;
  overage: { blockSize: number; monthlyUsd: number; annualUsd: number };
  trialDays: number;
  billingEnabled: boolean;
  mode: 'test' | 'live' | 'off';
}

class ApiClient {
  private baseUrl: string;
  private token: string | null = null;

  constructor() {
    // Use relative URLs in production, localhost in dev
    this.baseUrl = window.location.hostname === 'localhost'
      ? 'http://localhost:8787'
      : '';

    // Load token from localStorage if available
    this.token = localStorage.getItem('auth_token');
  }

  /**
   * Set authentication token
   */
  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  /**
   * Clear authentication token
   */
  clearToken(): void {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  /**
   * Get current token
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Check if user is authenticated
   */
  /**
   * The signed-in user as encoded in the session token (id, email, role, organization).
   * Synchronous; returns null when signed out or the token is malformed.
   */
  currentUser(): { id: string; email: string; role: string; organization_id: string; name?: string } | null {
    if (!this.token) return null;
    try {
      const payloadB64 = this.token.split('.')[1] || '';
      const json = atob(payloadB64.replace(/-/g, '+').replace(/_/g, '/'));
      const p = JSON.parse(json);
      if (!p.userId) return null;
      let name: string | undefined;
      try {
        name = JSON.parse(localStorage.getItem('user') || 'null')?.name;
      } catch {
        name = undefined;
      }
      return { id: p.userId, email: p.email, role: p.role, organization_id: p.organizationId, name };
    } catch {
      return null;
    }
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  /**
   * Generic fetch wrapper with error handling
   */
  private async fetch<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add auth token if available
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });

      // Parse response body
      const data = await response.json().catch(() => ({}));

      // Handle errors
      if (!response.ok) {
        const error = data as ApiError & { code?: string; upgrade_url?: string };
        const err = new Error(error.error || 'Request failed') as Error & { code?: string; status?: number; upgradeUrl?: string };
        err.code = error.code;
        err.status = response.status;
        err.upgradeUrl = error.upgrade_url;
        throw err;
      }

      return data as T;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // ==================== Generic verbs ====================
  // Used by pages that call endpoints without a dedicated typed method.

  async get<T = any>(endpoint: string): Promise<T> {
    return this.fetch<T>(endpoint, { method: 'GET' });
  }

  async post<T = any>(endpoint: string, body?: unknown): Promise<T> {
    return this.fetch<T>(endpoint, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) });
  }

  async put<T = any>(endpoint: string, body?: unknown): Promise<T> {
    return this.fetch<T>(endpoint, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) });
  }

  async delete<T = any>(endpoint: string, body?: unknown): Promise<T> {
    return this.fetch<T>(endpoint, { method: 'DELETE', body: body === undefined ? undefined : JSON.stringify(body) });
  }

  // ==================== Auth Endpoints ====================

  /** First-touch attribution written by the marketing site, if this browser saw it. */
  private attribution(): Record<string, string> | undefined {
    try {
      const raw = localStorage.getItem('fg_attr');
      if (!raw) return undefined;
      const a = JSON.parse(raw);
      return { source: a.source ?? '', medium: a.medium ?? '', campaign: a.campaign ?? '', referrer: a.referrer ?? '', landing: a.landing ?? '' };
    } catch {
      return undefined;
    }
  }

  async register(data: {
    organizationName: string;
    email: string;
    password: string;
    name: string;
    acceptTerms?: boolean;
    termsVersion?: string;
  }): Promise<AuthResponse> {
    const response = await this.fetch<AuthResponse>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ ...data, attribution: this.attribution() }),
    });
    this.setToken(response.token);
    try {
      localStorage.setItem('user', JSON.stringify({ ...response.user, organization: response.organization }));
    } catch {
      // storage may be unavailable
    }
    return response;
  }

  async login(data: {
    email: string;
    password: string;
  }): Promise<AuthResponse> {
    const response = await this.fetch<AuthResponse>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    this.setToken(response.token);
    try {
      localStorage.setItem('user', JSON.stringify({ ...response.user, organization: response.organization }));
    } catch {
      // storage may be unavailable
    }
    return response;
  }

  logout(): void {
    // Revoke the server-side session; ignore failures (already offline or expired)
    if (this.token) {
      this.fetch('/api/auth/logout', { method: 'POST' }).catch(() => undefined);
    }
    this.clearToken();
  }

  async forgotPassword(email: string): Promise<{ message: string; dev_reset_url?: string }> {
    return this.fetch('/api/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) });
  }

  async resetPassword(token: string, password: string): Promise<{ message: string }> {
    return this.fetch('/api/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    return this.fetch('/api/auth/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, newPassword }) });
  }

  async me(): Promise<{ user: AuthResponse['user']; organization: any }> {
    return this.fetch('/api/auth/me');
  }

  // ==================== Billing ====================

  async getBilling(): Promise<BillingInfo> {
    return this.fetch<BillingInfo>('/api/billing');
  }

  async startCheckout(plan: string, interval: 'month' | 'year'): Promise<{ url: string }> {
    return this.fetch('/api/billing/checkout', { method: 'POST', body: JSON.stringify({ plan, interval }) });
  }

  async openBillingPortal(): Promise<{ url: string }> {
    return this.fetch('/api/billing/portal', { method: 'POST' });
  }

  // ==================== Locations Endpoints ====================

  async getLocations(): Promise<{ locations: Location[] }> {
    return this.fetch<{ locations: Location[] }>('/api/locations');
  }

  async updateLocation(id: string, data: Partial<{ name: string; address: string; city: string; state: string; zip: string }>): Promise<Location> {
    return this.fetch<Location>(`/api/locations/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  async deleteLocation(id: string): Promise<void> {
    await this.fetch(`/api/locations/${id}`, { method: 'DELETE' });
  }

  async getLocation(id: string): Promise<Location> {
    return this.fetch<Location>(`/api/locations/${id}`);
  }

  async createLocation(data: {
    name: string;
    address: string;
    city: string;
    state: string;
    zip: string;
  }): Promise<Location> {
    return this.fetch<Location>('/api/locations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== Extinguishers Endpoints ====================

  async getExtinguishers(params?: {
    location_id?: string;
    status?: string;
    compliance_status?: string;
    /** Triage filters computed by describeCompliance() server-side. */
    reason?: string;
    actor?: 'staff' | 'contractor';
  }): Promise<{
    extinguishers: Extinguisher[];
    total?: number;
    byReason?: Record<string, number>;
    byActor?: Record<string, number>;
  }> {
    const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
    return this.fetch(`/api/extinguishers${query}`);
  }

  async getExtinguisher(id: string): Promise<Extinguisher> {
    return this.fetch<Extinguisher>(`/api/extinguishers/${id}`);
  }

  async getExtinguisherByQR(qrCode: string): Promise<Extinguisher> {
    return this.fetch<Extinguisher>(`/api/extinguishers/qr/${qrCode}`);
  }

  /** Dry run by default; pass commit to write. */
  async importExtinguishers(csv: string, commit = false): Promise<{
    parsed: number; willCreate: number; alreadyPresent: number; rejected: number;
    newLocations: string[]; ignoredColumns: string[];
    problems: Array<{ line: number; field: string; value: string; message: string }>;
    /** Rows that import but lost a value on the way in, e.g. an agent we cannot name. */
    warnings?: Array<{ line: number; field: string; value: string; message: string }>;
    planLimit: { limit: number; wouldTotal: number; overBy: number; upgradeUrl: string } | null;
    committed: boolean; created?: number;
  }> {
    return this.fetch('/api/extinguishers/import', { method: 'POST', body: JSON.stringify({ csv, commit }) });
  }

  async createExtinguisher(data: {
    qr_code: string;
    location_id: string;
    extinguisher_type: string;
    /** Optional: the fire class does not say whether the unit has a gauge. */
    agent?: string;
    size_lbs: number;
    room_area: string;
    install_date: string;
  }): Promise<Extinguisher> {
    return this.fetch<Extinguisher>('/api/extinguishers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateExtinguisher(
    id: string,
    data: Partial<Extinguisher>
  ): Promise<Extinguisher> {
    return this.fetch<Extinguisher>(`/api/extinguishers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteExtinguisher(id: string): Promise<void> {
    return this.fetch<void>(`/api/extinguishers/${id}`, {
      method: 'DELETE',
    });
  }

  // ==================== Inspections Endpoints ====================

  async getInspections(params?: {
    extinguisher_id?: string;
  }): Promise<{ inspections: Inspection[] }> {
    const query = params ? '?' + new URLSearchParams(params as any).toString() : '';
    return this.fetch<{ inspections: Inspection[] }>(`/api/inspections${query}`);
  }

  async createInspection(data: {
    extinguisher_id: string;
    inspection_type: 'monthly' | 'annual' | 'hydrostatic';
    inspection_date: string;
    pressure_gauge_status: 'good' | 'low' | 'high';
    seal_intact: boolean;
    findings?: string | null;
    photo_url?: string | null;
  }): Promise<Inspection> {
    return this.fetch<Inspection>('/api/inspections', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async analyzePhoto(
    file: File,
    extinguisherId: string
  ): Promise<{
    analysis: {
      pressure_gauge_status: string;
      seal_intact: boolean;
      confidence: number;
    };
    photo_url: string;
  }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('extinguisher_id', extinguisherId);

    const headers: HeadersInit = {};
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    const response = await fetch(`${this.baseUrl}/api/inspections/photo-analysis`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Photo analysis failed');
    }

    return response.json();
  }

  // ==================== User Management ====================

  /**
   * Get all users in the organization
   */
  async getUsers(): Promise<any> {
    return this.fetch<any>('/api/users');
  }

  /**
   * Get a specific user by ID
   */
  async getUser(userId: string): Promise<any> {
    return this.fetch<any>(`/api/users/${userId}`);
  }

  /**
   * Invite a new user
   */
  async inviteUser(data: { email: string; role: string }): Promise<any> {
    return this.fetch<any>('/api/users/invite', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Get pending invitations
   */
  async getInvitations(): Promise<any> {
    return this.fetch<any>('/api/users/invitations');
  }

  /**
   * Update user details
   */
  async updateUser(userId: string, data: any): Promise<any> {
    return this.fetch<any>(`/api/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  /**
   * Delete/deactivate user
   */
  async deleteUser(userId: string): Promise<any> {
    return this.fetch<any>(`/api/users/${userId}`, {
      method: 'DELETE'
    });
  }

  /**
   * Accept invitation (public endpoint)
   */
  async acceptInvitation(data: { token: string; password: string; name: string }): Promise<any> {
    return this.fetch<any>('/api/users/accept-invitation', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }
}

// Export singleton instance
export const api = new ApiClient();
